import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ViewEncapsulation,
  TemplateRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  signal,
  WritableSignal,
  effect,
  untracked,
  inject,
  DestroyRef
} from '@angular/core';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  NgSelectComponent,
  NgHeaderTemplateDirective,
  NgOptionTemplateDirective,
  NgFooterTemplateDirective,
  NgOptgroupTemplateDirective,
  NgPlaceholderTemplateDirective
} from '@ng-select/ng-select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export enum SelectMode {
  MULTI,
  SINGLE,
  ATTACHED_SINGLE,
  ATTACHED_MULTI
}

export interface SelectItem<T = string> {
  label: string;
  key: T;
  disabled?: boolean;
  icon?: string;
  group?: { label: string; order: number };
}

enum TemplateType {
  PLACEHOLDER,
  MULTI_LABEL,
  SINGLE_LABEL
}

interface TemplateContext {
  text: string;
  showIcon: boolean;
  iconPath?: string;
  containerClass: { [k: string]: boolean };
  textClass: string;
}

type SelectValue<T, M> = (M extends (SelectMode.MULTI | SelectMode.ATTACHED_MULTI) ? T[] | null : T | null);


@Component({
  selector: 'app-select-menu',
  standalone: true,
  imports: [
    CommonModule,
    NgSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NgHeaderTemplateDirective,
    NgOptionTemplateDirective,
    NgFooterTemplateDirective,
    NgOptgroupTemplateDirective,
    NgPlaceholderTemplateDirective,
    AssetVersionPipe
  ],
  templateUrl: './select-menu.component.html',
  styleUrl: './select-menu.component.scss',
  encapsulation: ViewEncapsulation.None
})


export class SelectMenuComponent<T = string, M extends SelectMode = SelectMode.SINGLE>
  implements OnInit, AfterViewInit, OnChanges {

  @Input({ required: true }) selectMode!: M;
  @Input({ required: true }) control!: FormControl<T | T[] | null>;
  @Input({ required: true }) items!: SelectItem<T>[];
  @Input() disabled = false;
  @Input() width = '100%';
  @Input() placeholder = 'Select...';
  @Input() iconPath?: string;
  @Input() iconStyle?: { [key: string]: any } = {};
  @Input() searchable = false;
  @Input() searchPlaceholder = 'Search item';
  @Input() showPlaceholderIcon = false;
  @Input() hideSelected = false;
  @Input() selectAllAndClearOptions = false;
  @Input() groupBySelected = false;
  @Input() showAllSelected = false;
  @Input() showSelectedIcon = false;
  @Input() showPlaceholderAllSelected = false;
  @Input() atLeastOneSelected = false;
  @Input() compareWith!: (option1: any, option2: any) => boolean;
  @Input() openPanel: WritableSignal<any> = signal(null);
  @Input() dropDownPosition: 'bottom' | 'top' | 'auto' = 'bottom';
  @Input() dropDownSide: 'left' | 'right' = 'right';
  @Input() dropDownWideWidth = false;
  @Input() customHeaderTemplate?: TemplateRef<any>;
  @Input() limitedHeight = false;
  @Input() showAllSelectedIcons = false;
  @Input() iconShape = 'default'; // use 'flag' for rectangle shape
  @Input() maxIconsAmount = 18;
  @Input() hasError = false;

  @Output() onApply = new EventEmitter<SelectValue<T, M>>();

  @ViewChild('ngSelect') readonly ngSelect!: NgSelectComponent;
  @ViewChild('multiLabel') multiLabel!: TemplateRef<any>;
  @ViewChild('singleLabel', { static: true }) singleLabel!: TemplateRef<any>;
  @ViewChild('placeholderTmp') placeholderTmp!: TemplateRef<any>;

  public filteredItems$!: WritableSignal<SelectItem<T>[]>;
  public previewsValue: SelectItem<T>[] = [];

  protected readonly TemplateType = TemplateType;
  protected readonly SelectMode = SelectMode;
  protected searchTerm = '';

  protected _tempSelected: SelectItem<T>[] | SelectItem<T> | null = null;
  protected _multiSelectedText = '';
  protected _currentTemplate!: TemplateRef<any>;

  private destroy$ = inject(DestroyRef);
  private multiSelectedItems: SelectItem<T>[] = [];
  private singleSelectedItems: SelectItem<T> | null = null;

  public isOpen: boolean = false;

  constructor(private cdr: ChangeDetectorRef) {
    effect(() => {
      const shouldOpen = this.openPanel();
      untracked(() => {
        if (shouldOpen) {
          this.ngSelect.open();
        } else {
          this.close(false, false);
        }
      });
    });
  }

  ngOnInit(): void {
    this.validateInput();
    const items = this.isMultiSelect && this.groupBySelected ?
      this.groupAndSort(this.items) : this.items;
    this.filteredItems$ = signal<SelectItem<T>[]>(items);
    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroy$))
      .subscribe(() => this.setValueFromControl());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      const items = this.isMultiSelect && this.groupBySelected ?
        this.groupAndSort(this.items) : this.items;
      this.filteredItems$ = signal<SelectItem<T>[]>(items);
      this.setValueFromControl();
    }
  }

  ngAfterViewInit(): void {
    this.updateDynamicTemplate();
    this.cdr.detectChanges();
  }



  public filterItems(term: string = ''): void {
    this.searchTerm = term;
    const filteredItems = term
      ? this.items.filter((item: SelectItem<T>) =>
        item.label.toLowerCase().includes(term.toLowerCase())
      )
      : this.items;

    this.filteredItems$.set(filteredItems);
  }

  public selectAll(): void {
    this._tempSelected = this.filteredItems$();
  }

  public clearAll(): void {
    this._tempSelected = [];
  }

  public apply(valueFromControl: boolean = false): void {
    const newFilteredItems =
      this.isMultiSelect && this.groupBySelected
        ? this.groupAndSort(this.items)
        : this.items;
    this.filteredItems$.set(newFilteredItems);
    this.selected = this._tempSelected;
    this.previewsValue = this.selected as SelectItem<T>[];

    if (this.isMultiSelect) {
      this._multiSelectedText = this.multiSelectedText;
    }
    if (!valueFromControl) {
      this.setControlValue();
      this.onApply.emit(this.getSelectedKeys());
    }
    this.updateDynamicTemplate();
  }

  public onCancel(): void {
    this.searchTerm = '';
    if (this.isMultiSelect) {
      this._tempSelected = this.previewsValue;
    }
  }

  protected groupFn(items: SelectItem<T>): string | undefined {
    return items.group?.label;
  }

  protected get isMultiSelect(): boolean {
    return this.selectMode === SelectMode.MULTI || this.selectMode === SelectMode.ATTACHED_MULTI;
  }

  protected get isAttachedSelect(): boolean {
    return this.selectMode === SelectMode.ATTACHED_SINGLE || this.selectMode === SelectMode.ATTACHED_MULTI;
  }

  protected get selectedAmount(): number {
    return (this.previewsValue as SelectItem<T>[]).length - 1;
  }

  protected get multiSelectedText(): string {
    return (
      this.selectedText +
      (this.selectedAmount && !this.showAllSelected
        ? ` + ${this.selectedAmount} `
        : '')
    );
  }

  protected get singleSelectedText(): string {
    return (
      this.items.find((item) => item === (this.selected as SelectItem<T>))?.label ??
      ''
    );
  }

  protected get selected(): SelectItem<T>[] | SelectItem<T> | null {
    return this.isMultiSelect ? this.multiSelectedItems : this.singleSelectedItems;
  }

  protected set selected(item: SelectItem<T> | SelectItem<T>[] | null) {
    if (this.isMultiSelect) {
      this.multiSelectedItems = item as SelectItem<T>[];
    } else {
      this.singleSelectedItems = item as SelectItem<T>;
    }
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = new AssetVersionPipe().transform('images/no-logo.png');
  }

  protected getTemplateContext(
    templateType: TemplateType,
    item?: SelectItem<T> | SelectItem<T>[] | null
  ): TemplateContext {
    switch (templateType) {
      case TemplateType.PLACEHOLDER:
        return {
          text: this.placeholder,
          showIcon: !!this.iconPath,
          iconPath: this.iconPath,
          containerClass: { 'placeholder-container': true, 'disabled': this.disabled },
          textClass: 'placeholder'
        };
      case TemplateType.MULTI_LABEL:
        return {
          text: this._multiSelectedText,
          showIcon: this.showPlaceholderIcon && !!this.iconPath,
          iconPath: this.iconPath,
          containerClass: { 'multi-value-container': true, 'disabled': this.disabled },
          textClass: 'multi-value'
        };
      case TemplateType.SINGLE_LABEL:
        item = item as SelectItem<T> | null;
        return {
          text: this.singleSelectedText,
          showIcon: this.showPlaceholderIcon || this.showSelectedIcon,
          iconPath: this.showPlaceholderIcon ? this.iconPath : item?.icon,
          containerClass: { 'single-value-container': true, 'disabled': this.disabled },
          textClass: 'single-value'
        };
    }
  }

  public close(isApply: boolean = false, callClose: boolean = true): void {
    isApply && this._tempSelected ? this.apply() : this.onCancel();
    if (this.ngSelect && callClose) {
      this.ngSelect.close();
    }
    this.isOpen = false;
  }

  protected onOpen(): void {
    this.isOpen = true;
  }

  handleItemClick(item: any, item$: any): void {
    if (this.selectMode === SelectMode.ATTACHED_SINGLE && item$?.selected) {
      this._tempSelected = item;
      this.close(true);
    }
  }

  protected singleSelect(): void {
    if (!this.isMultiSelect) {
      this.close(true);
    }
  }

  protected get disableApply(): boolean {
    return this.atLeastOneSelected && (this.isMultiSelect ? !(this._tempSelected as T[]).length : !!this._tempSelected);
  }

  private setControlValue(): void {
    const selectedKey = this.isMultiSelect
      ? (this.selected as SelectItem[]).map((item) => item.key) as T[]
      : (this.selected as SelectItem | null)?.key as T ?? null;

    this.control.setValue(selectedKey as SelectValue<T, M>);
  }

  private getGroupLabel(isSelected: boolean): string {
    return isSelected ? 'Selected Item' : 'Not Selected';
  }

  private groupAndSort(items: SelectItem<T>[]): SelectItem<T>[] {
    const selectedItems: SelectValue<T, M> = this._tempSelected as SelectValue<T, M>;
    const isArray = Array.isArray(selectedItems)
    return items
      .map((item) => {
        let isSelected: boolean = false
        if (isArray) {
          isSelected = selectedItems?.some(i => i.key === item.key);
        }
        return {
          label: item.label,
          key: item.key,
          icon: item.icon,
          ...(isArray && selectedItems.length
            ? {
              group: {
                label: this.getGroupLabel(isSelected),
                order: +!isSelected
              }
            }
            : {})
        };
      })
      .sort((a, b) => {
        const groupA = a.group?.order ?? Infinity;
        const groupB = b.group?.order ?? Infinity;

        if (groupA !== groupB) {
          return groupA - groupB;
        }
        if (a.label.toLowerCase() === "other") {
          return 1;
        }
        if (b.label.toLowerCase() === "other") {
          return -1;
        }
        return a.label.localeCompare(b.label);
      });
  }

  private get selectedText(): string {
    const selectedItems = this.previewsValue as SelectItem<T>[];
    return !selectedItems.length
      ? ''
      : this.showAllSelected
        ? selectedItems.map((item) => item.label).join(', ')
        : selectedItems[0].label;
  }

  private validateInput(): void {
    const isMulti = this.isMultiSelect;

    this.selectAllAndClearOptions = isMulti && this.selectAllAndClearOptions;
    this.groupBySelected = isMulti && this.groupBySelected;
    this.showAllSelected = isMulti && this.showAllSelected;
    this.showSelectedIcon = !isMulti && this.showSelectedIcon;
    this.hideSelected = !isMulti && this.hideSelected;
  }

  protected updateDynamicTemplate(): void {
    let templateToRender: TemplateRef<any>;
    if (this.isMultiSelect) {
      if (this.previewsValue.length > 0) {
        templateToRender =
          this.showPlaceholderAllSelected &&
            this.previewsValue.length === this.items.length
            ? this.placeholderTmp
            : this.multiLabel;
      } else {
        templateToRender = this.placeholderTmp;
      }
    } else {
      templateToRender = this.selected ? this.singleLabel : this.placeholderTmp;
    }
    this._currentTemplate = templateToRender;
  }

  private setValueFromControl(): void {
    this._tempSelected =
      (this.isMultiSelect
        ? this.items.filter(({ key }) =>
          (this.control.value as T[]).includes(key)
        )
        : this.items.find(({ key }) => key === this.control.value)) ??
      null;

    this.apply(true);
  }

  private getSelectedKeys(): SelectValue<T, M> {
    return this.isMultiSelect
      ? (this.selected as SelectItem<T>[]).map((item) => item.key) as SelectValue<T, M>
      : (this.selected as SelectItem<T>).key as SelectValue<T, M>;
  }
}
